import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, MessageCircle, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "new", title: "New", next: "preparing", nextLabel: "Start preparing" },
  { key: "preparing", title: "Preparing", next: "ready", nextLabel: "Mark ready" },
  { key: "ready", title: "Ready for pickup", next: "picked_up", nextLabel: "Picked up" },
];

const waPhone = (p) => { const d = (p || "").replace(/\D/g, ""); return d.startsWith("0") ? `92${d.slice(1)}` : d; };
const ago = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)}h ago`; };

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.15;
    o.start(); o.stop(ctx.currentTime + 0.25);
  } catch { /* audio blocked until first click */ }
}

export default function Orders() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("active");
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/orders?scope=${tab}`);
      setData(d);
      const n = d.orders.filter((o) => o.status === "new").length;
      if (tab === "active" && seen.current !== null && n > seen.current) { beep(); toast.success("New order!"); }
      if (tab === "active") seen.current = n;
    } catch { /* keep last data */ }
  }, [tab]);

  useEffect(() => { seen.current = null; load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.put(`/orders/${id}/status`, { status }); load(); } catch { toast.error("Couldn't update the order"); }
  };
  const toggleAccepting = async (v) => {
    setData((d) => ({ ...d, accepting_orders: v }));
    try { await api.put("/business/accepting-orders", { accepting: v }); toast.success(v ? "Taking orders" : "Orders paused"); } catch { load(); }
  };

  if (!data) return <div className="min-h-screen"><Navbar /><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></div>;

  const card = (o, col) => (
    <div key={o.order_id} className="bg-card border border-white/10 rounded-xl p-4" data-testid={`order-${o.token_no}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-display text-3xl font-extrabold">#{o.token_no}</span>
        <span className="text-xs text-muted-foreground">{ago(o.created_at)}</span>
      </div>
      <p className="text-sm font-medium mt-1">{o.customer_name} · <a className="text-primary" href={`tel:${o.phone}`}>{o.phone}</a></p>
      {o.pickup_time && <p className="text-xs text-muted-foreground">Pickup: {o.pickup_time}</p>}
      <ul className="mt-2 text-sm space-y-0.5">{o.items.map((i) => <li key={i.offer_id}>{i.qty} × {i.title}</li>)}</ul>
      {o.note && <p className="mt-2 text-xs bg-yellow-500/10 border border-yellow-500/30 rounded p-2">📝 {o.note}</p>}
      {o.total != null && <p className="mt-2 text-sm font-semibold">Rs {Number(o.total).toLocaleString()} <span className="text-xs text-muted-foreground font-normal">pay at pickup</span></p>}
      {col && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setStatus(o.order_id, col.next)} className="rounded-full bg-primary hover:bg-secondary" data-testid={`advance-${o.token_no}`}>{col.nextLabel}</Button>
          {o.status === "ready" && (
            <a href={`https://wa.me/${waPhone(o.phone)}?text=${encodeURIComponent(`Hi ${o.customer_name}, your order #${o.token_no} is ready for pickup!`)}`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="rounded-full border-white/15"><MessageCircle className="w-3 h-3 mr-1" /> Notify</Button>
            </a>
          )}
          <Button size="sm" variant="ghost" onClick={() => window.confirm(`Cancel order #${o.token_no}?`) && setStatus(o.order_id, "cancelled")} className="text-muted-foreground">Cancel</Button>
        </div>
      )}
      {!col && <p className="mt-2 text-xs capitalize text-muted-foreground">{o.status.replace("_", " ")}</p>}
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline text-primary">Orders</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Live orders</h1>
            <p className="text-sm text-muted-foreground mt-1">Today: {data.today_count} orders · Rs {Number(data.today_total).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-3 bg-card border border-white/10 rounded-full px-4 py-2" data-testid="accepting-toggle">
            <span className="text-sm">{data.accepting_orders ? "Taking orders" : "Orders paused"}</span>
            <Switch checked={data.accepting_orders} onCheckedChange={toggleAccepting} />
          </div>
        </div>

        {data.mode !== "food" && (
          <div className="mt-6 rounded-xl border border-white/10 bg-card p-5 text-sm flex items-start gap-3">
            <UtensilsCrossed className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <p>Pre-orders are off for your shop. Go to <a className="text-primary underline" href="/dashboard/business">Business → How customers buy</a> and pick <b>Food pre-orders</b> to let customers order ahead and get a token number.</p>
          </div>
        )}

        <div className="mt-6 flex gap-1 bg-card border border-white/10 rounded-full p-1 w-fit">
          {[["active", "Active"], ["today", "Today (all)"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm ${tab === k ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid={`orders-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "active" ? (
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {COLS.map((col) => {
              const list = data.orders.filter((o) => o.status === col.key);
              return (
                <div key={col.key} data-testid={`col-${col.key}`}>
                  <h3 className="font-display font-semibold mb-3">{col.title} <span className="text-muted-foreground text-sm">({list.length})</span></h3>
                  <div className="space-y-3">{list.length ? list.map((o) => card(o, col)) : <p className="text-sm text-muted-foreground border border-dashed border-white/10 rounded-xl p-6 text-center">Nothing here</p>}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{data.orders.length ? data.orders.map((o) => card(o, null)) : <p className="text-sm text-muted-foreground">No orders today yet.</p>}</div>
        )}
      </main>
    </div>
  );
}
